/**
 * @author       david.loehr@zumper.com (David Loehr)
 * @copyright    Copyright (c) 2023, Zumper
 */

import type { Values } from '@zcode/utility-types'

type PickWhereValueIncludesUndefined<T extends object> = Pick<
  T,
  Values<{ [Key in keyof T]: undefined extends T[Key] ? Key : never }>
>

type PickWhereValueNotUndefined<T extends object> = Pick<
  T,
  Values<{ [Key in keyof T]: undefined extends T[Key] ? never : Key }>
>

type KeysWhoseValueIsUndefined<Obj extends object> = {
  [P in keyof Obj]: Obj[P] extends never | undefined ? never : P
}

type RemoveUndefineds<Obj extends object> = Pick<
  Obj,
  KeysWhoseValueIsUndefined<Obj>[Exclude<keyof KeysWhoseValueIsUndefined<Obj>, undefined>]
>

export type MakeUndefinedsOptional<T extends object> = Partial<
  RemoveUndefineds<PickWhereValueIncludesUndefined<T>>
> &
  PickWhereValueNotUndefined<T>

export const deleteUndefineds = <T extends object>(obj: T): MakeUndefinedsOptional<T> => {
  // remove undefined props:
  for (const key of Object.keys(obj)) {
    // @ts-expect-error "`key` might not be a key of `allProps`"
    obj[key] === undefined && delete obj[key]
  }
  return obj
}
